import React, { useEffect, useState } from "react";
import {
  Accordion,
  Alert,
  Box,
  Button,
  Drawer,
  FileButton,
  Group,
  Loader,
  Stack,
  Switch,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import {
  IconCircleCheck,
  IconDownload,
  IconExclamationMark,
  IconUpload,
} from "@tabler/icons";
import { showNotification } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { Page, FormSectionCard } from "../../layout/page";
import PageListHeader from "../../components/pageListHeader";
import { useServerApi } from "../../hooks/userServerApi";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import { normalizeReportDownloadUrl } from "../../utils/openReport";

const TemplateCard = ({
  title,
  templateType,
  template,
  api,
  t,
  onUploaded,
}) => {
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewOpened, setPreviewOpened] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewDownloadUrl, setPreviewDownloadUrl] = useState("");
  const [placeholders, setPlaceholders] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.documentSetting.placeholders(templateType).then((result) => {
      if (!cancelled && result?.success) {
        setPlaceholders(result.data?.placeholders || []);
      }
    });
    return () => {
      cancelled = true;
    };
    // api is recreated each render; placeholders only depend on type
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateType]);

  const handleUpload = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      const result = await api.documentSetting.uploadTemplate(templateType, file);
      if (!result?.success) throw result;
      onUploaded?.(result.data?.documents);
      showNotification({
        title: t("Upload"),
        color: "green",
        icon: <IconCircleCheck size={18} />,
        message: t("Template uploaded"),
      });
    } catch (error) {
      showNotification({
        title: t("Upload"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: api.getErrorMsg?.(error?.error) || error?.message || t("Upload failed"),
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const { blob, filename } = await api.documentSetting.downloadTemplate(
        templateType
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (error) {
      showNotification({
        title: t("Download"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: error.message || t("Download failed"),
      });
    }
  };

  const closePreview = () => {
    setPreviewOpened(false);
    setPreviewUrl("");
    setPreviewDownloadUrl("");
    setPreviewing(false);
  };

  const handlePreview = async () => {
    try {
      setPreviewOpened(true);
      setPreviewing(true);
      setPreviewUrl("");
      setPreviewDownloadUrl("");

      const result = await api.documentSetting.preview(templateType, "pdf");
      if (!result?.success) throw result;
      const pdfUrl = normalizeReportDownloadUrl(result.data.url);
      setPreviewUrl(pdfUrl);
      setPreviewDownloadUrl(pdfUrl);
    } catch (error) {
      showNotification({
        title: t("Preview"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message:
          api.getErrorMsg?.(error?.error) ||
          error?.message ||
          t("Preview failed"),
      });
    } finally {
      setPreviewing(false);
    }
  };

  const handlePreviewDownload = () => {
    if (!previewDownloadUrl) return;
    const a = document.createElement("a");
    a.href = previewDownloadUrl;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.click();
  };

  return (
    <FormSectionCard>
      <Title order={5}>{title}</Title>
      <Text size="sm" mt="xs">
        {template?.currentName || t("No template")}
      </Text>
      {template?.uploadedAt && (
        <Text size="xs" color="dimmed">
          {t("Uploaded")}: {new Date(template.uploadedAt).toLocaleString()}
        </Text>
      )}
      {!template?.isUploaded && (
        <Text size="xs" color="dimmed">
          {t("Using bundled template")}: {template?.bundledName}
        </Text>
      )}
      <Group mt="md">
        <FileButton onChange={handleUpload} accept=".docx">
          {(props) => (
            <Button
              {...props}
              size="xs"
              leftIcon={<IconUpload size={14} />}
              loading={uploading}
            >
              {t("Upload Word template")}
            </Button>
          )}
        </FileButton>
        <Button
          size="xs"
          variant="default"
          leftIcon={<IconDownload size={14} />}
          onClick={handleDownload}
        >
          {t("Download current")}
        </Button>
        <Button
          size="xs"
          variant="light"
          loading={previewing}
          onClick={handlePreview}
        >
          {t("Preview")}
        </Button>
      </Group>
      <Drawer
        opened={previewOpened}
        onClose={closePreview}
        title={t("Sample preview")}
        padding="md"
        size={960}
        position="right"
      >
        <Stack spacing="sm" sx={{ height: "calc(100vh - 80px)" }}>
          <Group position="apart">
            <Text size="sm" color="dimmed">
              {t("Filled sample from dummy data")}
            </Text>
            <Button
              size="xs"
              variant="default"
              leftIcon={<IconDownload size={14} />}
              disabled={!previewDownloadUrl}
              onClick={handlePreviewDownload}
            >
              {t("Download PDF")}
            </Button>
          </Group>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              border: "1px solid var(--mantine-color-dark-4)",
              borderRadius: 8,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "var(--mantine-color-dark-7)",
            }}
          >
            {previewing && !previewUrl && <Loader />}
            {previewUrl && (
              <iframe
                title={`${templateType}-preview`}
                src={`${previewUrl}#navpanes=0&toolbar=1&view=FitH`}
                style={{
                  width: "100%",
                  height: "100%",
                  border: 0,
                  display: "block",
                }}
              />
            )}
          </Box>
        </Stack>
      </Drawer>
      <Accordion mt="md" variant="contained">
        <Accordion.Item value="placeholders">
          <Accordion.Control>
            {t("Available placeholders")}
          </Accordion.Control>
          <Accordion.Panel>
            <Stack spacing={4}>
              {placeholders.map((item) => (
                <Text key={item.tag} size="xs">
                  <Text span ff="monospace">
                    {item.tag}
                  </Text>
                  {" — "}
                  {item.description}
                </Text>
              ))}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </FormSectionCard>
  );
};

const DocumentSetting = () => {
  const [api] = useServerApi();
  const { t } = useTranslation();
  const tabNav = useTabNavigate();
  const [docs, setDocs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const setField = (path, value) => {
    setDocs((prev) => {
      const next = _.cloneDeep(prev);
      _.set(next, path, value);
      return next;
    });
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const result = await api.documentSetting.get();
      if (result?.success) setDocs(result.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      const result = await api.documentSetting.save(docs);
      if (!result?.success) throw result;
      setDocs(result.data);
      showNotification({
        title: t("Save"),
        color: "green",
        icon: <IconCircleCheck size={18} />,
        message: t("settings.saved"),
      });
    } catch (error) {
      showNotification({
        title: t("Save"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: api.getErrorMsg?.(error?.error) || t("settings.saveFailed"),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page>
      <PageListHeader
        title={t("Document Settings")}
        actionButtons={[
          {
            label: t("Save"),
            onClick: handleSave,
            loading: saving,
            disabled: loading || !docs,
          },
          {
            label: t("Close"),
            onClick: () => tabNav.closeActive(),
            variant: "default",
          },
        ]}
      />
      {docs && (
        <Tabs defaultValue="debitNote">
          <Tabs.List>
            <Tabs.Tab value="debitNote">{t("Debit note")}</Tabs.Tab>
            <Tabs.Tab value="receipt">{t("Receipt")}</Tabs.Tab>
            <Tabs.Tab value="renewal">{t("Renewal")}</Tabs.Tab>
            <Tabs.Tab value="quotation">{t("Quotation")}</Tabs.Tab>
            <Tabs.Tab value="clientStatement">{t("Client statement")}</Tabs.Tab>
            <Tabs.Tab value="orderSubmissionCoverSheet">
              {t("Submit cover sheet")}
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="debitNote" pt="md">
            <Stack>
              <TemplateCard
                title={t("Debit note Word template")}
                templateType="debitNote"
                template={docs.debitNote?.template}
                api={api}
                t={t}
                onUploaded={setDocs}
              />
              <FormSectionCard>
                <Title order={5}>{t("Titles")}</Title>
                <TextInput
                  mt="sm"
                  label={t("Title")}
                  value={docs.debitNote?.title || ""}
                  onChange={(e) => setField("debitNote.title", e.currentTarget.value)}
                />
                <TextInput
                  mt="sm"
                  label={t("Chinese title")}
                  value={docs.debitNote?.title_cn || ""}
                  onChange={(e) =>
                    setField("debitNote.title_cn", e.currentTarget.value)
                  }
                />
                <TextInput
                  mt="sm"
                  label={t("Credit note title")}
                  value={docs.creditNote?.title || ""}
                  onChange={(e) => setField("creditNote.title", e.currentTarget.value)}
                />
                <TextInput
                  mt="sm"
                  label={t("Credit note Chinese title")}
                  value={docs.creditNote?.title_cn || ""}
                  onChange={(e) =>
                    setField("creditNote.title_cn", e.currentTarget.value)
                  }
                />
              </FormSectionCard>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="receipt" pt="md">
            <Stack>
              <TemplateCard
                title={t("Receipt Word template")}
                templateType="receipt"
                template={docs.receipt?.template}
                api={api}
                t={t}
                onUploaded={setDocs}
              />
              <FormSectionCard>
                <Title order={5}>{t("Receipt options")}</Title>
                <TextInput
                  mt="sm"
                  label={t("Title")}
                  value={docs.receipt?.title || ""}
                  onChange={(e) => setField("receipt.title", e.currentTarget.value)}
                />
                <TextInput
                  mt="sm"
                  label={t("Chinese title")}
                  value={docs.receipt?.title_cn || ""}
                  onChange={(e) =>
                    setField("receipt.title_cn", e.currentTarget.value)
                  }
                />
                <TextInput
                  mt="sm"
                  label={t("Thank-you message")}
                  value={docs.receipt?.thankMessage || ""}
                  onChange={(e) =>
                    setField("receipt.thankMessage", e.currentTarget.value)
                  }
                />
                <Textarea
                  mt="sm"
                  minRows={4}
                  label={t("Terms (one per line)")}
                  value={(docs.receipt?.terms || []).join("\n")}
                  onChange={(e) =>
                    setField(
                      "receipt.terms",
                      e.currentTarget.value.split("\n")
                    )
                  }
                />
              </FormSectionCard>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="renewal" pt="md">
            <Stack>
              <TemplateCard
                title={t("Renewal Word template")}
                templateType="renewal"
                template={docs.renewal?.template}
                api={api}
                t={t}
                onUploaded={setDocs}
              />
              <FormSectionCard>
                <Title order={5}>{t("Titles")}</Title>
                <TextInput
                  mt="sm"
                  label={t("Title")}
                  value={docs.renewal?.title || ""}
                  onChange={(e) => setField("renewal.title", e.currentTarget.value)}
                />
                <TextInput
                  mt="sm"
                  label={t("Chinese title")}
                  value={docs.renewal?.title_cn || ""}
                  onChange={(e) =>
                    setField("renewal.title_cn", e.currentTarget.value)
                  }
                />
              </FormSectionCard>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="quotation" pt="md">
            <Stack>
              <TemplateCard
                title={t("Quotation Word template")}
                templateType="quotation"
                template={docs.quotation?.template}
                api={api}
                t={t}
                onUploaded={setDocs}
              />
              <TemplateCard
                title={t("Confirmation letter Word template")}
                templateType="quotationConfirmLetter"
                template={docs.quotation?.confirmationLetterTemplate}
                api={api}
                t={t}
                onUploaded={setDocs}
              />
              <FormSectionCard>
                <Title order={5}>{t("Quotation options")}</Title>
                <Switch
                  mt="sm"
                  label={t("Print confirmation letter on confirm")}
                  checked={!!docs.quotation?.printConfirmationLetter}
                  onChange={(e) =>
                    setField(
                      "quotation.printConfirmationLetter",
                      e.currentTarget.checked
                    )
                  }
                />
              </FormSectionCard>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="clientStatement" pt="md">
            <Stack>
              <TemplateCard
                title={t("Client statement Word template")}
                templateType="clientStatement"
                template={docs.clientStatement?.template}
                api={api}
                t={t}
                onUploaded={setDocs}
              />
              <FormSectionCard>
                <Title order={5}>{t("Client statement options")}</Title>
                <Switch
                  mt="sm"
                  label={t("Only include dispatched policies")}
                  checked={!!docs.clientStatement?.needDispatchPolicyStatement}
                  onChange={(e) =>
                    setField(
                      "clientStatement.needDispatchPolicyStatement",
                      e.currentTarget.checked
                    )
                  }
                />
              </FormSectionCard>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="orderSubmissionCoverSheet" pt="md">
            <Stack>
              <TemplateCard
                title={t("Submit cover sheet Word template")}
                templateType="orderSubmissionCoverSheet"
                template={docs.orderSubmissionCoverSheet?.template}
                api={api}
                t={t}
                onUploaded={setDocs}
              />
              <FormSectionCard>
                <Title order={5}>{t("Titles")}</Title>
                <TextInput
                  mt="sm"
                  label={t("Title")}
                  value={docs.orderSubmissionCoverSheet?.title || ""}
                  onChange={(e) =>
                    setField(
                      "orderSubmissionCoverSheet.title",
                      e.currentTarget.value
                    )
                  }
                />
              </FormSectionCard>
            </Stack>
          </Tabs.Panel>
        </Tabs>
      )}
      {!loading && !docs && (
        <Alert color="red">{t("Could not load document settings")}</Alert>
      )}
    </Page>
  );
};

export default DocumentSetting;
